import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import useSEO from "../hooks/useSEO";
import Lightbox from "../components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalise event videos to {url, poster}
const normVid = (v) => (typeof v === "string" ? { url: v, poster: "" } : { url: v.url, poster: v.poster || "" });

const VIDEO_PLACEHOLDER = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNjAwIDkwMCI+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJnIiB4MT0iMCIgeTE9IjAiIHgyPSIxIiB5Mj0iMSI+PHN0b3Agb2Zmc2V0PSIwIiBzdG9wLWNvbG9yPSIjMDAzRjcyIi8+PHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMENBM0IwIi8+PC9saW5lYXJHcmFkaWVudD48L2RlZnM+PHJlY3Qgd2lkdGg9IjE2MDAiIGhlaWdodD0iOTAwIiBmaWxsPSJ1cmwoI2cpIi8+PGNpcmNsZSBjeD0iODAwIiBjeT0iNDUwIiByPSIxMDAiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xNSIvPjxwb2x5Z29uIHBvaW50cz0iNzcwLDQwMCA3NzAsNTAwIDg2MCw0NTAiIGZpbGw9IiNmZmYiLz48L3N2Zz4=";

const GalleryPage = () => {
  const [albums, setAlbums] = useState([]);
  const [eventAlbums, setEventAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [playingVid, setPlayingVid] = useState(null);
  const { t, tx } = useLanguage();
  const location = useLocation();

  useSEO({
    title: tx("Galeria - Momente nga Eventet dhe Trajnimet"),
    description: tx("Shikoni galerine e Albanian Aligners Academy - foto nga eventet, trajnimet, workshop-et dhe masterclass-et tona me dentiste dhe eksperte nderkombetare."),
    keywords: "galeri foto, evente dentare, foto trajnime, albanian aligners academy galeria",
    canonical: "https://aligners.academy/galeria",
  });

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setAlbums(r.data)).catch(console.error);
    axios.get(`${API}/events`).then((r) => {
      const withMedia = r.data.filter((e) => (e.gallery_images && e.gallery_images.length > 0) || (e.gallery_videos && e.gallery_videos.length > 0));
      setEventAlbums(withMedia.map((e) => ({
        id: `event-${e.id}`,
        title: e.title,
        cover_image: (e.gallery_images && e.gallery_images[0]) || e.image_url,
        images: e.gallery_images || [],
        videos: (e.gallery_videos || []).map(normVid),
        event_name: e.date,
        source: "event",
      })));
    }).catch(console.error);
  }, []);

  // Auto-open album from ?album=ID query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const albumParam = params.get("album");
    if (albumParam) setSelectedAlbum(albumParam);
  }, [location.search]);

  const allAlbums = [
    ...albums.map((a) => ({ ...a, source: "gallery", videos: [] })),
    ...eventAlbums,
  ];
  const viewing = selectedAlbum ? allAlbums.find((a) => a.id === selectedAlbum) : null;

  const closeAlbum = () => {
    setSelectedAlbum(null);
    // remove query param from URL
    if (location.search) {
      window.history.replaceState({}, "", location.pathname);
    }
  };

  return (
    <div data-testid="gallery-page">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&auto=format)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(130deg, rgba(60,212,225,0.85) 0%, rgba(0,63,114,0.92) 100%)" }} />
        <div className="relative z-10 text-center text-white px-6 max-w-[1350px] mx-auto">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{t("gallery_title")}</h1>
          <p className="text-white/80 text-base md:text-lg">{t("gallery_subtitle")}</p>
        </div>
      </section>

      <section className="py-16 px-6 max-w-[1350px] mx-auto">
        {viewing ? (
          <div data-testid="gallery-album-view">
            <button onClick={closeAlbum} className="mb-6 text-sm text-[#1CA3B0] font-semibold inline-flex items-center gap-2 hover:underline" data-testid="back-to-gallery">
              <i className="fas fa-arrow-left"></i> {t("gallery_title")}
            </button>
            <h2 className="text-2xl font-bold mb-2 font-['Inter_Tight']">{viewing.title}</h2>
            {viewing.event_name && <p className="text-sm text-[#828D9E] mb-6">{viewing.event_name}</p>}

            {/* Videos first (if any) */}
            {viewing.videos && viewing.videos.length > 0 && (
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#1CA3B0] mb-3">{tx("Video")}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {viewing.videos.map((v, i) => (
                    <div key={i} className="rounded-xl overflow-hidden shadow-sm border border-slate-200 relative bg-[#0a0f1a]" data-testid={`gallery-video-${i}`}>
                      <video
                        controls={playingVid === i}
                        preload="metadata"
                        poster={v.poster || VIDEO_PLACEHOLDER}
                        className="w-full block aspect-video object-cover"
                        onPlay={() => setPlayingVid(i)}
                        onPause={() => setPlayingVid(null)}
                      >
                        <source src={v.url} />
                      </video>
                      {playingVid !== i && (
                        <button
                          type="button"
                          onClick={(e) => {
                            const vid = e.currentTarget.previousElementSibling;
                            if (vid && vid.tagName === "VIDEO") vid.play();
                          }}
                          className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/15 transition-colors"
                          aria-label={tx("Luaj video")}
                        >
                          <span className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #3CD4E1, #1CA3B0)", boxShadow: "0 8px 24px rgba(60,212,225,0.45)" }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="#003F72"><path d="M8 5v14l11-7z" /></svg>
                          </span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photos */}
            {viewing.images && viewing.images.length > 0 && (
              <div>
                {viewing.videos && viewing.videos.length > 0 && <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#1CA3B0] mb-3">{tx("Foto")}</p>}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {viewing.images.map((img, i) => (
                    <img key={i} src={img} alt="" loading="lazy" className="rounded-xl w-full h-56 object-cover cursor-pointer hover:opacity-80 transition-all shadow-sm hover:shadow-md" onClick={() => setLightboxIndex(i)} data-testid={`gallery-thumb-${i}`} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            {allAlbums.length === 0 ? (
              <p className="text-center text-[#828D9E] py-16 text-lg">{t("gallery_empty")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allAlbums.map((album) => (
                  <div key={album.id} className="group cursor-pointer" onClick={() => setSelectedAlbum(album.id)} data-testid={`album-${album.id}`}>
                    <div className="relative overflow-hidden rounded-xl shadow-sm">
                      <img src={album.cover_image || (album.images && album.images[0]) || ""} alt={album.title} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-5">
                        <div>
                          <p className="text-white font-bold text-base">{album.title}</p>
                          <p className="text-white/70 text-xs mt-0.5">
                            {(album.images || []).length} {t("gallery_photos")}
                            {(album.videos || []).length > 0 ? ` · ${(album.videos || []).length} video` : ""}
                          </p>
                          {album.source === "event" && <span className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1CA3B0]/20 text-white">{tx("Event")}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {lightboxIndex !== null && viewing && viewing.images && (
        <Lightbox
          images={viewing.images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}
    </div>
  );
};

export default GalleryPage;
